/**
 * React hook that is used to mark the block wrapper element.
 * It provides all the necessary props like the class name.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-block-editor/#useblockprops
 */
import { useEffect, useState, useCallback } from "@wordpress/element";

/**
 * Lets webpack process CSS, SASS or SCSS files referenced in JavaScript files.
 * Those files can contain any CSS code that gets applied to the editor.
 *
 * @see https://www.npmjs.com/package/@wordpress/scripts#using-css
 */
import { fetchData } from "../api";
import { Spinner } from "@wordpress/components";
import Pagination from "./Pagination.component";
import BSHelper from "../BSHelper";

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @return {Element} Element to render.
 */
export default function Reports() {
	const [libraries, setLibraries] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const itemsPerPage = 5;
	const arrowClassIcon =
		" transition-all " +
		(BSHelper.isPageRTL() ? "fa-arrow-left-long" : "fa-arrow-right-long");

	const fetchLibraries = useCallback(
		async (page = 1) => {
			setLoading(true);
			setError(null);

			try {
				// console.log(`Fetching libraries for page ${page}`);
				const response = await fetchData(
					`/bseitwp/v1/libraries/filter/pdf?options[paginate]=1&options[limit]=${itemsPerPage}&options[page]=${page}`,
				);
				// console.log("API Response:", response);
				setLibraries(response.data.data);
				setTotalPages(response.data.pagination.total_pages);
			} catch (error) {
				setError(error);
			} finally {
				setLoading(false);
			}
		},
		[itemsPerPage],
	);

	useEffect(() => {
		fetchLibraries(currentPage);
	}, [fetchLibraries, currentPage]);

	const handlePageChange = (page) => {
		setCurrentPage(page);
	};

	return (
		<>
			<div className="md:px-5 lg:px-0">
				{loading && (
					<div className="flex items-center justify-center w-full">
						<Spinner
							style={{
								height: "calc(2px * 20)",
								width: "calc(2px * 20)",
							}}
						/>
					</div>
				)}

				{error && <p>{error.message}</p>}

				{!loading &&
					libraries.map((item, index) => (
						<div
							key={index}
							className="block transition rounded-lg hover:bg-lightGray"
						>
							<div className="grid items-center justify-between mb-5 md:grid-cols-10">
								<div className="lg:col-span-1 md:col-span-1">
									<a
										target="_blank"
										href={item.meta_data.pdf ?? "#"}
										className="block text-3xl font-bold lg:text-5xl lg:text-center"
									>
										{(currentPage - 1) * itemsPerPage + index + 1 < 10
											? `0${(currentPage - 1) * itemsPerPage + index + 1}`
											: (currentPage - 1) * itemsPerPage + index + 1}
									</a>
								</div>
								<div className="lg:col-span-2 md:col-span-3">
									<a
										target="_blank"
										href={item.meta_data.pdf ?? "#"}
										className="block group my-5 transition rounded-lg w-40 h-52 lg:w-[200px] lg:h-[248px] lg:mx-auto lg:block overflow-hidden"
									>
										<img
											className="object-cover w-full h-full transition group-hover:scale-110"
											src={item.thumbnail}
											alt={item.title}
											loading="lazy"
										/>
									</a>
								</div>
								<div className="pb-5 lg:col-span-6 md:col-span-5">
									<div className="lg:px-12">
										<h3 className="text-xl font-bold lg:text-3xl">
											<a
												target="_blank"
												href={item.meta_data.pdf ?? "#"}
												className="block"
											>
												{BSHelper.truncate(item.title, 60)}
											</a>
										</h3>
										<a
											target="_blank"
											href={item.meta_data.pdf ?? "#"}
											className="block pt-5 text-darkGray"
										>
											{BSHelper.truncate(item.meta_data.short_description, 140)}
										</a>
									</div>
								</div>
								<div className="text-center lg:col-span-1 md:col-span-1">
									<a
										target="_blank"
										href={item.meta_data.pdf ?? "#"}
										className="inline-flex items-center justify-center w-12 h-12 p-2 text-white transition-all rounded-full ltr:-rotate-45 bg-primary rtl:rotate-45 hover:rotate-0 group"
									>
										<i className={`fa-solid ${arrowClassIcon} group-hover:rotate-0`}></i>
									</a>
								</div>
							</div>
						</div>
					))}

				{!loading && totalPages > 1 && (
					<Pagination
						currentPage={currentPage}
						totalPages={totalPages}
						onPageChange={handlePageChange}
					/>
				)}
			</div>
		</>
	);
}
